import { getStudentData } from '../assets/data';
import { getLessonListForStudent, updateLessonStatusById, getLessonsFromLocalStorage, saveLessonsToLocalStorage } from '../utils/lessonsService';
import { saveScheduleItem, updateScheduleStatusById } from '../utils/scheduleService';

// Extracted from StudentProfile.js's renderUpcomingLessons closure (Phase
// 3 of the platform-health plan: getting the file under ~600 lines needs
// pulling logic into hooks, not just JSX into components). This is the
// Schedule tab's own status-change handler (source: 'schedule' — the one
// that IS allowed to drive auto-charging, via the lessonStatusUpdated
// event useScheduleSync listens for) plus the edit/add-lesson modal
// openers used by the desktop week-grid and mobile timeline.
export function useScheduleTabActions({
  studentId,
  isNew,
  upcomingLessons,
  studentLessons,
  setStudentLessons,
  setStatusChangeLoading,
  updateUpcomingLessons,
  setStudentData,
  setEditingScheduleLesson,
  setEditScheduleLessonTitle,
  setEditScheduleLessonDate,
  setEditScheduleLessonFrom,
  setEditScheduleLessonTo,
  setEditScheduleLessonAllDay,
  setEditScheduleLessonStatus,
  setShowEditScheduleLessonModal,
  setSelectedDateForLesson,
  setAddLessonItemType,
  setAddLessonTitle,
  setAddLessonFrom,
  setAddLessonTo,
  setAddLessonRepeatWeekly,
  setAddLessonAllDay,
  setAddLessonErrors,
  setShowAddLessonModal,
}) {
  // Функция для изменения статуса урока
  const handleLessonStatusChange = async (lessonId, newStatus) => {
    // Находим урок для обновления
    const lessonToUpdate = upcomingLessons.find(lesson => lesson.id === lessonId) ||
                           studentLessons.find(lesson => lesson.id === lessonId);
    if (!lessonToUpdate) {
      console.error('Lesson not found:', lessonId);
      return;
    }
    setStatusChangeLoading(true);
    try {
    const allLessonsData = getLessonsFromLocalStorage();
    const updatedLessonsData = allLessonsData.map(lesson =>
      lesson.id === lessonId ? { ...lesson, status: newStatus } : lesson
    );
    saveLessonsToLocalStorage(updatedLessonsData);

    // Обновляем в Supabase
    // Проверяем, является ли это уроком из lessons или из schedule
    // Если урок из schedule (не имеет полного контента), обновляем только в localStorage
    const hasFullContent = lessonToUpdate.containers || lessonToUpdate.editorContents || lessonToUpdate.whiteboards;

    if (hasFullContent) {
      // Это урок из lessons - обновляем в Supabase. Только статус —
      // lessonToUpdate здесь взят из upcomingLessons/studentLessons
      // (лёгкий список без content), полноценный saveLesson() перезаписал
      // бы content урока пустым значением и стирал бы его.
      try {
        await updateLessonStatusById(lessonId, newStatus);
        console.log('Lesson status updated in Supabase:', lessonId, 'to', newStatus);
      } catch (error) {
        console.error('Error updating lesson status in Supabase:', error);
      }
      // Mirror the status onto the matching schedule row if there is one.
      // saveLesson alone only updates the lessons table; Home's money widget
      // aggregates from the schedule table, so without this sync it would
      // miss the change. Silently no-ops if no schedule row matches.
      updateScheduleStatusById(lessonId, newStatus).catch(() => {});
    } else {
      // Это запись из schedule — обновляем в Supabase (schedule)
      try {
        await saveScheduleItem({ ...lessonToUpdate, status: newStatus });
        console.log('Schedule lesson status updated in Supabase:', lessonId, 'to', newStatus);
      } catch (err) {
        console.error('Error updating schedule lesson status:', err);
      }
    }

    // Broadcast to drive the charge add/remove in a single place — the
    // window listener at the top of this component. Doing the auto-charge
    // here inline as well would double-charge (the listener runs first,
    // writes the charge, then the inline path reads localStorage and
    // appends a second one). prevStatus is captured from the snapshot
    // taken at the start of the handler so the listener can decide
    // add-vs-remove without racing the ref update.
    window.dispatchEvent(new CustomEvent('lessonStatusUpdated', {
      detail: { lessonId, status: newStatus, prevStatus: lessonToUpdate.status, source: 'schedule' }
    }));

    // Обновляем состояние компонента
    setStudentData(prev => ({ ...prev }));

    // Принудительно обновляем список уроков после изменения статуса
    if (studentId && studentId !== "new" && !isNew) {
      const currentStudent = getStudentData()[studentId];
      const studentIdForLessons = currentStudent?.studentId || currentStudent?.id || studentId;
      if (studentIdForLessons) {
        setTimeout(() => {
          getLessonListForStudent(studentIdForLessons).then(lessons => {
          setStudentLessons(lessons);
          updateUpcomingLessons(); // Обновляем upcoming lessons
          }).catch(error => {
            console.error('Error loading lessons:', error);
          });
        }, 100);
      }
    }
    } finally {
      setStatusChangeLoading(false);
    }
  };

  // Shared by the desktop week-grid chip and the mobile timeline card —
  // both open the same edit modal for a lesson.
  const openEditScheduleLessonModal = (lesson) => {
    let dateString = '';
    if (lesson.date) {
      if (/^\d{4}-\d{2}-\d{2}$/.test(lesson.date)) {
        dateString = lesson.date;
      } else {
        const lessonDate = new Date(lesson.date);
        if (!isNaN(lessonDate.getTime())) {
          dateString = `${lessonDate.getFullYear()}-${String(lessonDate.getMonth() + 1).padStart(2, '0')}-${String(lessonDate.getDate()).padStart(2, '0')}`;
        }
      }
    }
    setEditingScheduleLesson(lesson);
    setEditScheduleLessonTitle(lesson.title || '');
    setEditScheduleLessonDate(dateString);
    setEditScheduleLessonFrom(lesson.from || lesson.timeFrom || '09:00');
    setEditScheduleLessonTo(lesson.to || lesson.timeTo || '10:00');
    setEditScheduleLessonAllDay(lesson.allDay || false);
    setEditScheduleLessonStatus(lesson.status || 'in_progress');
    setShowEditScheduleLessonModal(true);
  };

  const handleAddLesson = (date) => {
    setSelectedDateForLesson(date);
    setAddLessonItemType('lesson');
    setAddLessonTitle('');
    setAddLessonFrom('09:00');
    setAddLessonTo('10:00');
    setAddLessonRepeatWeekly(false);
    setAddLessonAllDay(false);
    setAddLessonErrors([]);
    setShowAddLessonModal(true);
  };

  return { handleLessonStatusChange, openEditScheduleLessonModal, handleAddLesson };
}
